import styled from 'styled-components';
import { mq } from '../../theme/mediaQueries';

export const StyledDashboardHero = styled.section`
  position: relative;
  padding: var(--s-7) var(--s-7) var(--s-6);
  margin-bottom: var(--s-6);
  border-radius: var(--r-xl);
  background: linear-gradient(135deg, var(--brand-ink) 0%, #1F1B3D 100%);
  overflow: hidden;
  > * { position: relative; z-index: 1; }
`;

export const StyledHeroRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const StyledHeroEyebrow = styled.span`
  display: inline-block;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: var(--brand-lime);
`;

export const StyledHeroTitle = styled.h2`
  margin: var(--s-2) 0 var(--s-1);
  font-size: var(--t-4xl);
  font-weight: var(--weight-bold);
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--surface);

  ${mq.ltSm} { font-size: var(--t-2xl); }
`;

export const StyledHeroSub = styled.p`
  margin: 0;
  max-width: 38rem;
  font-size: var(--t-base);
  color: rgba(255,255,255,.65);
`;

export const StyledHeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const StyledHeroHint = styled.kbd`
  font: inherit;
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  padding: var(--s-1) var(--s-2);
  border-radius: var(--r-xs);
  border: 1px solid rgba(255,255,255,.15);
  background-color: rgba(255,255,255,.08);
  color: rgba(255,255,255,.65);
`;

export const StyledDashboardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 20px;
  margin-top: 4px;

  ${mq.ltLg} { grid-template-columns: 1fr; }
`;

export const StyledDashboardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

export const StyledFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: var(--s-2) var(--s-3);
  background: var(--surface);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
`;

export const StyledFilterLabel = styled.span`
  font-size: 11px;
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: var(--muted);
  margin-right: 4px;
`;

export const StyledFilterCitySelect = styled.div`
  min-width: 176px;
`;

export const StyledTopSellingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
`;

export const StyledTopSellingRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: var(--r-md);
  background: var(--paper);
  border: 1px solid var(--line);
  gap: 14px;
`;

export const StyledTopSellingIconTile = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--r-sm);
  background: var(--paper-2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  svg {
    width: 18px;
    height: 18px;
  }
`;

export const StyledTopSellingMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledTopSellingName = styled.span`
  font-size: var(--t-md);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledTopSellingSku = styled.span`
  font-size: var(--t-xs);
  font-family: var(--font-mono);
  color: var(--muted);
`;

export const StyledTopSellingUnits = styled.div`
  flex-shrink: 0;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
`;

export const StyledTopSellingCount = styled.span`
  font-size: var(--t-base);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
`;

export const StyledTopSellingLabel = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
`;

export const StyledTopSellingEmpty = styled.div`
  padding: var(--s-6) var(--s-2);
  text-align: center;
  font-size: var(--t-sm);
  color: var(--muted);
`;

export const StyledTopSellingPlaceholderRow = styled.div`
  height: 56px;
  border-radius: var(--r-md);
  background: var(--paper);
  border: 1px solid var(--line);
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

export const StyledKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--s-4);
  margin-bottom: var(--s-6);

  ${mq.ltLg} { grid-template-columns: repeat(2, 1fr); }
  ${mq.ltSm} { grid-template-columns: repeat(2, 1fr); }
`;

export const StyledFinancialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--s-4);

  ${mq.ltLg} { grid-template-columns: 1fr; }
`;

export const StyledFinancialCard = styled.div`
  padding: var(--s-4);
  border-radius: var(--r-md);
  background: var(--paper);
  border: 1px solid var(--line);
`;

export const StyledFinancialLabel = styled.div`
  font-size: 11px;
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: var(--muted);
`;

export const StyledFinancialValue = styled.div`
  font-size: var(--t-2xl);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-top: 6px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
`;

export const StyledSeeAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
  gap: var(--s-1);
  transition: color var(--dur-fast) var(--ease-out);
  white-space: nowrap;

  &:hover {
    color: var(--ink);
  }
`;

export const StyledBottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: var(--s-4);

  ${mq.ltLg} { grid-template-columns: 1fr; }
`;

export const StyledCityLegend = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
`;

export const StyledCityLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--t-sm);
`;

export const StyledCityDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const StyledCityName = styled.span`
  flex: 1;
  min-width: 0;
  color: var(--ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledCityPct = styled.span`
  font-variant-numeric: tabular-nums;
  color: var(--muted);
  font-weight: 500;
`;
